import React, { FC, useEffect, useMemo, useState, useRef } from 'react';
import NextLink from 'next/link';
import { useRouter } from 'next/navigation';
import { FaCheck } from 'react-icons/fa';
import { Variant } from 'shared/types/product';
import { Product } from 'shared/types/product/Product';
import { LineItem } from 'shared/types/wishlist/LineItem';
import Modal from 'components/commercetools-ui/atoms/modal';
import Typography from 'components/commercetools-ui/atoms/typography';
import WishlistButton from 'components/commercetools-ui/organisms/wishlist/components/wishlist-button';
import { useFormat } from 'helpers/hooks/useFormat';
import useMediaQuery from 'helpers/hooks/useMediaQuery';
import useVariantWithDiscount from 'helpers/hooks/useVariantWithDiscount';
import { desktop } from 'helpers/utils/screensizes';
import { useAccount, useCart } from 'frontastic';
import Image, { ImageProps } from 'frontastic/lib/image';
import Link from '../../atoms/link';
import CustomfitOptions from '../product/product-details/components/customfit-options';
import { discardRepeatedValues } from '../product/product-details/helpers/discardRepeatedValues';
import Prices from '../product/product-tile/prices';
import useTrack from '../product/product-tile/useTrack';
import CustomfitVariant from '../product/product-details/components/customfit-variant';
import { getCurrencyCookie } from 'helpers/utils/getCurrencyCookie';
import { promotionCategory } from '../product/product-details/types';
import { SmartLink } from 'components/commercetools-ui/atoms/smart-link';
import { isLightColor } from 'helpers/utils/isLightColor';
interface YouMayAlsoLikeTileProps {
  product: Product;
  productLogo?: ImageProps;
  onClick?: () => void;
  isSearchResult?: boolean;
  disableQuickView?: boolean;
  disableWishlistButton?: boolean;
  disableVariants?: boolean;
  isChanged: boolean;
  setIsChanged: (value: boolean) => void;
}

export const CheckedSwatch = ({
  imgUrl,
  altText = '',
  sku,
  isActive,
  colourHexCode,
}: {
  imgUrl: string | undefined;
  altText?: string;
  sku?: string;
  isActive: boolean;
  colourHexCode?: string;
}) => {
  const tickColor = isLightColor(colourHexCode) ? 'black' : 'white';
  const multiColor = colourHexCode?.includes('_') ? colourHexCode?.split('_') : '';
  return (
    <div className="relative h-[22px] w-[22px] cursor-pointer">
      <div
        className="h-full w-full border border-[#dadada]"
        style={{
          backgroundColor: colourHexCode,
          backgroundImage: multiColor ? `linear-gradient(90deg, ${multiColor?.join(',')})` : '',
        }}
      />
      {isActive && (
        <svg
          className="absolute left-1/2 top-1/2 h-[12px] w-[12px] -translate-x-1/2 -translate-y-1/2"
          viewBox="0 0 24 24"
          fill="none"
          stroke={tickColor || '#000'}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      )}
    </div>
  );
};

export const CenterImageCropSwatch = ({
  imgUrl,
  altText = '',
  sku,
  isActive,
}: {
  imgUrl: string | undefined;
  altText?: string;
  sku: string;
  isActive: boolean;
}) => {
  return (
    <div
      className="relative h-4 w-4 origin-top-left cursor-pointer overflow-hidden"
      style={{ transform: 'scale(5.5)' }}
    >
      <Image
        src={imgUrl}
        alt={altText}
        style={{ minWidth: '100vw' }}
        className="absolute left-1/2 top-1/2 h-screen w-screen max-w-full -translate-x-1/2 -translate-y-1/2 object-cover"
      />
      {isActive && (
        <div
          className="relative h-[22px] w-[22px] bg-transparent"
          style={{ transform: 'scale(calc(1 / 5.5))', transformOrigin: 'left top' }}
        >
          <div className="absolute left-[8px] top-[3px] h-[10px] w-[6px] rotate-45 border-b-2 border-r-2 border-solid border-white" />
        </div>
      )}
    </div>
  );
};
const YouMayAlsoLikeTile: FC<YouMayAlsoLikeTileProps> = ({
  product,
  productLogo,
  onClick,
  isSearchResult = false,
  disableQuickView = false,
  disableWishlistButton = false,
  disableVariants = false,
  isChanged,
  setIsChanged,
}) => {
  const [isDesktopSize] = useMediaQuery(desktop);
  const { ref } = useTrack({ product });
  const variantWithDiscount = useVariantWithDiscount(product.variants) as Variant;
  const [isOpenModal, setIsOpenModal] = useState(false);
  const [selectedVariant, setSelectedVariant] = useState(() => variantWithDiscount ?? product?.variants[0]);
  const [hoveredVariant, setHoveredVariant] = useState<Variant | null>(null);
  const [selectedColour, setSelectedColour] = useState<string | null>(
    variantWithDiscount ? variantWithDiscount?.attributes?.colour : product?.variants[0]?.attributes?.colour || null,
  );
  const [selectedSize, setSelectedSize] = useState<string | null>(null);
  const [storedVariants, setStoredVariants] = useState<any[]>([]);
  const { formatMessage: formatCartMessage } = useFormat({ name: 'cart' });
  const { formatMessage: formatProductMessage } = useFormat({ name: 'product' });
  const [isSizeGuideModalOpen, setIsSizeGuideModalOpen] = useState(false);
  const sizeOverlayContentsRef = useRef<HTMLIFrameElement | null>(null);
  const { addItem } = useCart();
  const { logout, account: isLoggedIn } = useAccount();
  const router = useRouter();
  const productRefs = useRef<(HTMLDivElement | null)[]>([]);
  const [maxHeight, setMaxHeight] = useState(0);
  const [isCartDisable, setIsCartDisable] = useState<boolean>(false);
  const [customFitPrice, setCustomFitPrice] = useState<number | null>(
    product?.customFitDetails?.CF_Price?.GBP as number,
  );

  const localeSelection = getCurrencyCookie('locale_selection');
  const currency: string | null = localeSelection?.currency || null;

  useEffect(() => {
    // Retrieve stored variants from localStorage
    const retrievedVariants = JSON.parse(localStorage.getItem('viewedVariants') || '[]');
    setStoredVariants(retrievedVariants);

    // Calculate the maximum height for product refs
    if (productRefs.current.length > 0) {
      const heights = productRefs.current.map((ref) => ref?.offsetHeight || 0);
      const tallest = Math.max(...heights);
      setMaxHeight(tallest);
    }

    // Handle selected colour and size to find the correct variant
    if (selectedColour && selectedSize && product?.variants) {
      const foundVariant = product.variants.find(
        (variant: any) => variant.attributes.colour === selectedColour && variant.attributes.size === selectedSize,
      );

      if (foundVariant && foundVariant !== selectedVariant) {
        setSelectedVariant(foundVariant);

        const updatedVariants = retrievedVariants.map((storedItem: any) =>
          storedItem.product.name === product?.name ? { ...storedItem, variant: foundVariant } : storedItem,
        );

        if (JSON.stringify(updatedVariants) !== JSON.stringify(retrievedVariants)) {
          localStorage.setItem('viewedVariants', JSON.stringify(updatedVariants));
          setStoredVariants(updatedVariants);
          setIsChanged(!isChanged);
        }
      }
    }
  }, [product, selectedColour, selectedSize, selectedVariant, isChanged, setIsChanged]);
  const addtowishlist = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoggedIn) {
      router.push('/login');
    }
  };

  const productToWishlistLineItem = useMemo<LineItem | undefined>(() => {
    if (product) {
      return {
        lineItemId: product.productId ?? '',
        productId: product.productId,
        name: product.name,
        count: 1,
        variant: selectedVariant,
        addedAt: new Date(),
        _url: product._url,
      };
    }
  }, [product, selectedVariant]);

  const handleColourSelect = (variant: Variant) => setSelectedColour(variant?.attributes?.colour);
  const handleSizeSelect = (size: string) => setSelectedSize(size);

  // Filter unique color variants
  const uniqueColorVariants = useMemo(() => {
    const seenColours = new Set();
    return product.variants.filter((variant: Variant) => {
      const colour = variant?.attributes?.colour;
      if (!colour || seenColours.has(colour)) {
        return false;
      }
      seenColours.add(colour);
      return true;
    });
  }, [product.variants]);

  // Filter unique size variants based on selected color
  const uniqueSizeVariants = useMemo(() => {
    const seenSizes = new Set();
    return product.variants
      .filter((variant: Variant) => variant?.attributes?.colour === selectedColour)
      .map((variant: Variant) => variant?.attributes?.size)
      .filter((size) => size && !seenSizes.has(size))
      .map((size) => {
        seenSizes.add(size);
        return size;
      });
  }, [product.variants, selectedColour]);

  const renderSizes = () => {
    const filteredSizes = product?.variants.filter((v: any) => v.attributes.colour === selectedColour);
    const sizes = discardRepeatedValues(filteredSizes, 'size').map((variant: any) => variant.attributes.size);

    return (
      sizes.length > 0 && (
        <>
          <div className="w-full">
            <span className="block pb-2 font-bold">
              {' '}
              {formatProductMessage({
                id: 'choose.size',
                defaultMessage: 'Choose Size:',
              })}
            </span>
          </div>
          <div className="mt-4 flex flex-wrap gap-4">
            {sizes.map((size: string, index: number) => (
              <div key={index} className="mr-2">
                <span
                  className={`block h-auto w-auto cursor-pointer rounded border border-slate-500 p-5 transition-transform hover:bg-[#2dac70] hover:text-white ${
                    selectedSize === size ? 'bg-[#2dac70] text-white' : ''
                  }`}
                  onClick={() => handleSizeSelect(size)}
                  title={size}
                >
                  {size}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-10 w-full">
            <span
              onClick={showSizeGuide}
              className="flex cursor-pointer items-center text-12 font-bold hover:text-green-600"
            >
              Size guide
              <span className="ml-2 pt-2 font-bold text-green-600">&gt;</span>
            </span>
          </div>
        </>
      )
    );
  };

  const displayedImage = hoveredVariant?.images?.[0] || selectedVariant.images?.[0] || productLogo?.media?.file;

  const toggleModal = () => {
    setIsOpenModal(!isOpenModal);
  };

  const handleAddToCart = () => {
    if (selectedVariant) {
      addItem(selectedVariant, 1);
    }
  };

  const showSizeGuide = () => {
    const iframe = document.createElement('iframe');
    sizeOverlayContentsRef.current = iframe;
    iframe.style.cssText = `height: 100%; width: 100%`;
    iframe.src = `https://www.americangolf.co.uk/on/demandware.store/Sites-AmericanGolf-GB-Site/en_GB/Product-SizeGuide?pid=${selectedVariant.sku}`;
    setIsSizeGuideModalOpen(true);
  };

  // const customFitVariant = product?.variants?.[0]?.attributes?.isCustomFit;

  // useEffect(() => {
  //   if (customFitVariant) {
  //     setIsCartDisable(true);
  //   }
  // }, []);

  useEffect(() => {
    if (currency === 'EUR') {
      setCustomFitPrice(product?.customFitDetails?.CF_Price?.EUR || null);
    } else if (currency === 'GBP') {
      setCustomFitPrice(product?.customFitDetails?.CF_Price?.GBP || null);
    }
  }, [currency, product]);
  const convertProductNameToUrl = (input: string | undefined) => {
    const urlformat = `${input?.toLowerCase().replace(/\s+/g, '-')}`;
    const url = urlformat?.endsWith('-') ? urlformat : `${urlformat}-`;
    return url?.replace(/,/g, '');
  };
  const promotionCategory = product?.categories as promotionCategory[] | undefined;

  const newArrivals = promotionCategory?.find((i: promotionCategory) => i?.name === 'New Arrivals');
  return (
    <div onClick={onClick} className="mx-2 flex min-h-[700px] w-[100%] flex-col items-start justify-between px-3">
      <div className="grow">
        <SmartLink
          href={`${product?.categories?.[0]?._name}/${
            convertProductNameToUrl(product?.name) + product?.slug + '.html'
          }`}
          className="block shrink-0 grow basis-0"
        >
          <div className="p-3">
            <div className="relative mb-4 flex w-[236px] items-center justify-center sm:w-[296px] lg:w-[300px]">
              <img src={product?.variants[0]?.images?.[0]} width={240} alt={product?.name} height={240} />
              {newArrivals && (
                <button className="absolute right-10 top-8 h-[72px] w-[72px] rounded-full bg-[#127E57] p-5 font-body text-[22px] font-extrabold leading-4 text-white">
                  {'New'}
                </button>
              )}
            </div>
            <div className="w-full selection:text-start">
              <p className="font-body text-16 font-semibold leading-[19.2px]">{product?.name}</p>
            </div>
          </div>
        </SmartLink>
      </div>
      <div className="mb-10 mt-4 text-start">
        <Prices price={product?.variants?.[0].price} discountedPrice={product.variants?.[0].discountedPrice} />
      </div>
      <div className="w-full ">
        <SmartLink href={`/${product?._url}`}>
          <button
            className="h-[48px] w-[295px] rounded-md bg-[#43d6a2] p-4 font-body text-16
              font-bold uppercase leading-[19.2px] text-black"
          >
            VIEW PRODUCT
          </button>
        </SmartLink>
      </div>
    </div>
  );
};

export default YouMayAlsoLikeTile;
